"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import {
  beginPublicFormAttempt,
  endPublicFormAttempt,
  getPublicFormRetrySeconds,
  PUBLIC_FORM_MIN_AGE_MS,
  type PublicFormAction,
} from "@/shared/lib/publicFormGuard";

export function usePublicFormGuard(action: PublicFormAction, { active = true } = {}) {
  const honeypotRef = useRef<HTMLInputElement>(null);
  const openedAt = useRef<number | null>(null);
  const pending = useRef(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [retryAfterSeconds, setRetryAfterSeconds] = useState(0);

  useEffect(() => {
    if (!active) return;
    openedAt.current = Date.now();
    setError(null);
    const updateCooldown = () => setRetryAfterSeconds(getPublicFormRetrySeconds(action));
    updateCooldown();
    const timer = window.setInterval(updateCooldown, 1_000);
    window.addEventListener("storage", updateCooldown);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener("storage", updateCooldown);
      openedAt.current = null;
    };
  }, [action, active]);

  const beginAttempt = useCallback(() => {
    if (!active || pending.current) return false;
    setError(null);
    // Read the DOM value so autofill without a React change event is caught too.
    if (honeypotRef.current?.value) return false;
    if (openedAt.current === null || Date.now() - openedAt.current < PUBLIC_FORM_MIN_AGE_MS) {
      setError("Please take a moment to review your details, then try again.");
      return false;
    }
    if (!beginPublicFormAttempt(action)) {
      const retry = getPublicFormRetrySeconds(action);
      setRetryAfterSeconds(retry);
      if (retry === 0) setError("Please wait for your current request to finish.");
      return false;
    }
    pending.current = true;
    setIsSubmitting(true);
    setRetryAfterSeconds(getPublicFormRetrySeconds(action));
    return true;
  }, [action, active]);

  const endAttempt = useCallback(() => {
    if (!pending.current) return;
    endPublicFormAttempt(action);
    pending.current = false;
    setIsSubmitting(false);
  }, [action]);

  return { honeypotRef, beginAttempt, endAttempt, error, retryAfterSeconds, isSubmitting };
}
